import { useCallback, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Pager, ListStates, SortableTh } from './SyncStatusBrowser';
import type {
  ListResult,
  TransactionListQueryParams,
} from './types';

/**
 * Shared primitives for the M19 T2 transactional list views
 * (Charges / Invoices / Disputes / Refunds / Subscription Changes).
 *
 * Unlike the M10b sync-status browsers, these tables have NO sync_state — they
 * are append-only mirrors of Stripe transactional objects. Each view supports
 * a domain `status` filter (index-backed) + free-text search + pagination.
 *
 * The shared chrome here is a search box + an optional status dropdown + the
 * pager/empty/loading states. The date-range filter, universal customer
 * typeahead, and saved-view presets are deferred to T3 (Session 3).
 */

/**
 * Hook: manage the status/search/pagination state for a transactional list
 * view and fetch the matching page from the backend.
 *
 * @param basePath     The API path, e.g. 'admin/charges'.
 * @param statusKey    When set, the filter key sent for the status dropdown
 *                     ('status' for charges/invoices/disputes/refunds, omitted
 *                     for subscription-changes which uses 'deltaType').
 * @param pageSize     Rows per page (default 25).
 * @param extraParams  M19 T3: view-specific extra query params merged into the
 *                     request (e.g. the disputes evidence-due SLA filter). The
 *                     view owns this state and passes it in; the hook refetches
 *                     on change. Keys with undefined values are omitted.
 */
export function useTransactionList<T>(
  basePath: string,
  statusKey: 'status' | 'deltaType',
  pageSize = 25,
  extraParams: Record<string, string | number | undefined> = {}
) {
  const [status, setStatus] = useState<string>('all');
  const [offset, setOffset] = useState(0);
  // M19 T3: universal Customer filter (cus_ id selected from the typeahead)
  // + the shared date-range filter (from/to Unix seconds).
  const [customer, setCustomer] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ from?: number; to?: number; }>(
    {}
  );
  // Column sort (server-side, allowlisted in the handler).
  const [sort, setSort] = useState<string | null>(null);
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');

  const params = useMemo(
    () => ({
      ...(status !== 'all' ? { [statusKey]: status } : {}),
      limit: pageSize,
      offset,
      customer: customer ?? undefined,
      from: dateRange.from,
      to: dateRange.to,
      sort: sort ?? undefined,
      dir: sort ? dir : undefined,
      // M19 T3: view-specific extra params (e.g. disputes evidenceDueBefore).
      ...extraParams,
    } as TransactionListQueryParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status, statusKey, pageSize, offset, customer, dateRange, extraParams, sort, dir]
  );

  const buildQuery = useCallback(
    (p: TransactionListQueryParams) => {
      const qs = new URLSearchParams();
      const statusVal = p[statusKey];
      if (statusVal) qs.set(statusKey, statusVal);
      if (p.limit) qs.set('limit', String(p.limit));
      if (p.offset) qs.set('offset', String(p.offset));
      if (p.customer) qs.set('customer', p.customer);
      if (p.from != null) qs.set('from', String(p.from));
      if (p.to != null) qs.set('to', String(p.to));
      if (p.evidenceDueBefore != null) {
        qs.set('evidenceDueBefore', String(p.evidenceDueBefore));
      }
      if (p.sort) qs.set('sort', p.sort);
      if (p.sort && p.dir) qs.set('dir', p.dir);
      const suffix = qs.toString();
      return suffix ? `${basePath}?${suffix}` : basePath;
    },
    [basePath, statusKey]
  );

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      api.get<ListResult<T>>(buildQuery(params), { signal }),
    [buildQuery, params]
  );
  const { data, loading, error, refetch } = useAsync(fetcher, [
    params.status,
    params.deltaType,
    params.limit,
    params.offset,
    params.customer,
    params.from,
    params.to,
    params.evidenceDueBefore,
    params.sort,
    params.dir,
  ]);

  /** Toggle a sortable column: same column → flip dir; new column → desc. */
  const toggleSort = useCallback(
    (key: string) => {
      setDir((prevDir) => (sort === key ? (prevDir === 'desc' ? 'asc' : 'desc') : 'desc'));
      setSort(key);
      setOffset(0);
    },
    [sort]
  );

  return {
    data,
    loading,
    error,
    refetch,
    status,
    setStatus,
    offset,
    setOffset,
    pageSize,
    customer,
    setCustomer,
    dateRange,
    setDateRange,
    // Column sort.
    sort,
    dir,
    toggleSort,
  };
}

/** Status-option set for a view's dropdown. */
export interface StatusOption {
  value: string;
  label: string;
  tone: BadgeTone;
}

type BadgeTone = 'neutral' | 'success' | 'warn' | 'danger' | 'brand';

const TONE_CLASSES: Record<BadgeTone, string> = {
  neutral: 'bg-ink-100 text-ink-600',
  success: 'bg-success-500/10 text-success-600',
  warn: 'bg-warn-500/10 text-warn-600',
  danger: 'bg-danger-500/10 text-danger-600',
  brand: 'bg-brand-50 text-brand-600',
};

/** A small color-coded status pill. Falls back to neutral for unknown values. */
export function StatusBadge({
  status,
  options,
}: {
  status: string;
  options: StatusOption[];
}) {
  const match = options.find((o) => o.value === status);
  const cls = match ? TONE_CLASSES[match.tone] : TONE_CLASSES.neutral;
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {status}
    </span>
  );
}

/**
 * Filter toolbar for a transactional list view. Renders the status dropdown
 * only — the generic free-text search box was removed (QA: it was unclear
 * what it searched; the universal Customer typeahead + date-range filter are
 * the replacement). The status dropdown is omitted when the view passes no
 * options.
 */
export function TransactionListToolbar({
  status,
  setStatus,
  statusOptions,
  statusLabel,
}: {
  status: string;
  setStatus: (s: string) => void;
  /** The set of selectable statuses. When empty, the dropdown is omitted. */
  statusOptions: StatusOption[];
  statusLabel: string;
}) {
  if (statusOptions.length === 0) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-2 text-sm text-ink-600">
        <span className="text-xs font-medium uppercase tracking-wide text-ink-500">
          {statusLabel}
        </span>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="input max-w-[12rem] text-sm"
        >
          <option value="all">All</option>
          {statusOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

// Re-export the shared list chrome so each view imports from one place.
export { Pager, ListStates, SortableTh };
